import type { CarouselSlide as CarouselSlideType } from '@smartapply/shared';

interface CarouselSlideProps {
  slide: CarouselSlideType;
  totalSlides: number;
  /** Quando true, renderiza no tamanho real 1080x1350 pra export PDF. */
  exportMode?: boolean;
}

/**
 * Slide de carrossel LinkedIn em formato 4:5 (1080×1350px).
 * Estilo replica a estética da app: bg paper, serif Newsreader, accent saffron.
 *
 * O accentText é renderizado em saffron com underline manual via SVG abaixo.
 * Match é case-sensitive substring no title.
 */
export function CarouselSlide({ slide, totalSlides, exportMode }: CarouselSlideProps) {
  const total = totalSlides.toString().padStart(2, '0');
  const idx = slide.index.toString().padStart(2, '0');

  // Quebra o título em [antes, accent, depois] pra renderizar o destaque.
  const titleSegments = splitTitleByAccent(slide.title, slide.accentText);

  // Dimensões: exportMode usa o tamanho real do LinkedIn carousel (1080×1350,
  // proporção 4:5). Em preview, escalonamos pra caber na tela mantendo a
  // proporção via aspect-ratio + width responsivo.
  const containerStyle: React.CSSProperties = exportMode
    ? {
        width: '1080px',
        height: '1350px',
        backgroundColor: '#f4efe6',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: "'Newsreader', 'Source Serif Pro', Georgia, 'Times New Roman', serif",
      }
    : {
        aspectRatio: '4 / 5',
        backgroundColor: '#f4efe6',
        position: 'relative',
        overflow: 'hidden',
        fontFamily: "'Newsreader', 'Source Serif Pro', Georgia, 'Times New Roman', serif",
      };

  // Escala interna: tudo é dimensionado em % do container pra funcionar tanto
  // em 1080×1350 (export) quanto em preview menor.
  const titleFontSize = exportMode ? '88px' : '7.5cqi';
  const bodyFontSize = exportMode ? '36px' : '3.2cqi';
  const captionFontSize = exportMode ? '20px' : '1.7cqi';
  const numeratorFontSize = exportMode ? '22px' : '1.9cqi';
  const sidePadding = exportMode ? '96px' : '8.5%';

  return (
    <div style={{ ...containerStyle, containerType: 'inline-size' }}>
      {/* Numerador no topo direito */}
      <div
        style={{
          position: 'absolute',
          top: exportMode ? '80px' : '6.5%',
          right: sidePadding,
          fontFamily: "'JetBrains Mono', 'IBM Plex Mono', Menlo, monospace",
          fontSize: numeratorFontSize,
          color: '#1b1f23',
          letterSpacing: '0.05em',
          fontWeight: 400,
        }}
      >
        {idx}<span style={{ color: '#c5862e' }}>/{total}</span>
      </div>

      {/* Bloco central: título + body + caption */}
      <div
        style={{
          position: 'absolute',
          top: '50%',
          left: sidePadding,
          right: sidePadding,
          transform: 'translateY(-50%)',
          display: 'flex',
          flexDirection: 'column',
          gap: exportMode ? '32px' : '2.8%',
        }}
      >
        <h1
          style={{
            fontFamily: "'Newsreader', 'Source Serif Pro', Georgia, serif",
            fontSize: titleFontSize,
            lineHeight: 1.05,
            color: '#1b1f23',
            fontWeight: 500,
            letterSpacing: '-0.015em',
            margin: 0,
            wordWrap: 'break-word',
          }}
        >
          {titleSegments.map((seg, i) =>
            seg.accent ? (
              <span
                key={i}
                style={{
                  color: '#c5862e',
                  position: 'relative',
                  display: 'inline-block',
                  paddingBottom: '0.05em',
                }}
              >
                {seg.text}
                <span
                  aria-hidden
                  style={{
                    position: 'absolute',
                    left: '0',
                    right: '0',
                    bottom: '0.02em',
                    height: exportMode ? '8px' : '0.7cqi',
                    backgroundColor: '#c5862e',
                    opacity: 0.4,
                    borderRadius: '2px',
                  }}
                />
              </span>
            ) : (
              <span key={i}>{seg.text}</span>
            ),
          )}
        </h1>

        {slide.body && (
          <p
            style={{
              fontFamily: "'Newsreader', 'Source Serif Pro', Georgia, serif",
              fontSize: bodyFontSize,
              lineHeight: 1.45,
              color: '#3d3f44',
              fontWeight: 400,
              margin: 0,
              maxWidth: exportMode ? '780px' : '85%',
            }}
          >
            {slide.body}
          </p>
        )}

        {slide.caption && (
          <>
            <div
              style={{
                width: exportMode ? '120px' : '11%',
                height: exportMode ? '3px' : '0.3cqi',
                backgroundColor: '#c5862e',
                marginTop: exportMode ? '32px' : '2.8%',
              }}
            />
            <p
              style={{
                fontFamily: "'Inter', -apple-system, sans-serif",
                fontSize: captionFontSize,
                lineHeight: 1.5,
                color: '#1b1f23',
                fontWeight: 500,
                letterSpacing: '0.15em',
                textTransform: 'uppercase',
                margin: 0,
                maxWidth: exportMode ? '600px' : '70%',
              }}
            >
              {slide.caption}
            </p>
          </>
        )}
      </div>
    </div>
  );
}

interface TitleSegment {
  text: string;
  accent: boolean;
}

/**
 * Quebra o título em segmentos pra destacar accentText em saffron.
 * Case-sensitive substring match. Se accentText não bater no título,
 * retorna o título inteiro como single segment sem accent.
 */
function splitTitleByAccent(title: string, accentText: string | null): TitleSegment[] {
  if (!accentText) return [{ text: title, accent: false }];
  const idx = title.indexOf(accentText);
  if (idx === -1) return [{ text: title, accent: false }];
  const before = title.slice(0, idx);
  const after = title.slice(idx + accentText.length);
  const segments: TitleSegment[] = [];
  if (before) segments.push({ text: before, accent: false });
  segments.push({ text: accentText, accent: true });
  if (after) segments.push({ text: after, accent: false });
  return segments;
}
